import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import useAxios from "../../hooks/useAxios";
import useCommonActions from "../../hooks/useCommonActions";
import { ContentLoader, PageToolbar, Pagination, TooltipWrapper, RatingStar } from "../common";
import badges from "../../_helpers/badgeHelper";
import helpers from "../../_helpers/common";
import { hasAuthorization } from "../../_helpers/hasAuthorization";
import SearchForm from "./partial/SearchForm";
import CommonList from "../common/CommonList";
import { PageContentSearchFormState, PageContent } from "../../types/interfaces";

const List: React.FC = () => {
  const axiosInstance = useAxios();
  const { deleteItem } = useCommonActions();
  const [searchForm, setSearchForm] = useState<PageContentSearchFormState>({ search_string: "", status: "" });
  const [results, setResults] = useState<PageContent[] | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10 });
  const [isLoading, setIsLoading] = useState(false);
  const isInitialRender = useRef(false);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    getListData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination]);
  
  /*Handle pagination data change*/
  const handlePageChange = (page: number, pageSize: number) => {
    setPagination({ currentPage: page, pageSize });
  };

  const getListData = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const response: any = await axiosInstance({
        url: "pages/list",
        method: "GET",
        data: {
          page: pagination.currentPage,
          limit: pagination.pageSize,
          search_string: searchForm.search_string,
          status: searchForm.status,
        },
      });
      // console.log("event list response", response);
	  if(!response.error){
        setResults(response.data.pages.docs);
        setTotalItems(response.data.pages.totalDocs);
	  }
    } catch (error: any) {
      console.error("Error in api request:", error);
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[pagination,searchForm]);
  
  /*Delete selected item*/
  const deletePage = async (itemid?: string) => {
    const message = `Are you sure you want to delete the page?`;
    deleteItem(`pages/delete/${itemid}`, message, getListData);
  };

  return (
   <>
      <div className="d-flex justify-content-between">
        {/*Page toolbar Area Start*/}
        <PageToolbar title="Page List" />
		{hasAuthorization("add", "page_content") && (
          <Link
			className="btn btn-flex btn-primary h-40px fs-7 fw-bold"
			to={`/create-update-page/0`}
		  >
			Create Page
		  </Link>
		)}
      </div>
	  <div id="kt_content" className="content flex-column-fluid">
        {/*Search Area Start*/}
        <SearchForm searchForm={searchForm} setSearchForm={setSearchForm} handleSearchClick={getListData}/>
        {/*Search Area End*/}
        {/*Content Area Start*/}
        <div className={`card ${isLoading ? "overlay overlay-block" : ""}`}  style={{ minHeight:"70vh", maxHeight: "70vh" }} >
          <div className="card-body pt-0 pb-0 table-responsive">
		    <table className="table table-hover table-row-bordered table-row-solid gy-4 align-middle fw-bold w-100">
			  <thead
				style={{
				  position: "sticky",
				  top: 0,
				  zIndex: 5,
				  backgroundColor: `${isLoading ? "#BBBCBE" : "white"}`,
				  boxShadow: "0 4px 2px -2px rgba(190, 190, 190, 0.2)",
				}}
			  >
				<tr className="text-start text-gray-800 fw-bold fs-7 text-uppercase gs-0">
				  {/* <th
					  className="w-10px pe-2 dt-orderable-none"
					  data-dt-column="0"
					  rowSpan={1}
					  colSpan={1}
					  aria-label=""
					>
					  <span className="dt-column-title">
						<div className="form-check form-check-sm form-check-custom form-check-solid me-3">
						  <input
							className="form-check-input"
							type="checkbox"
							data-kt-check="true"
							data-kt-check-target="#kt_ecommerce_products_table .form-check-input"
							value="1"
						  />
						</div>
					  </span>
					  <span className="dt-column-order"></span>
					</th> */}
				  <th className="min-w-250px">Page Name</th>
				  <th className="">Title</th>
				  <th className="text-end">Actions</th>
				</tr>
			  </thead>
			  <tbody className="fw-semibold text-gray-600">
				{!results || results.length === 0 ? (
				  <tr className="text-center">
					<td colSpan={10}>No Content Found</td>
				  </tr>
				) : (
				  results.map((item) => (
					<tr key={item._id}>
					  <td>
						<div className="d-flex align-items-center">
						  <div className="symbol symbol-square symbol-55px overflow-hidden me-3">
							<div className="symbol-label">
							  <img
								src={item?.bannerpath}
								alt={item?.title}
								className="w-100"
							  />
							</div>
						  </div>
						  <div className="d-flex flex-column justify-content-center">
							<TooltipWrapper title={helpers.capitalizeFirstLetterOfEachWord(item?.title)} placement="bottom">
							  <span
								className="fw-semibold fs-6 text-gray-500 d-inline-block"
							  >
								{helpers.capitalizeFirstLetterOfEachWord(item?.title) || "N/A"}
							  </span>
							</TooltipWrapper>
						  </div>
						</div>
					  </td>			  
					  <td>
						{helpers.convertDateTime(
						  item?.created_at,
						  "DD MMM YYYY"
						) || "N/A"}
					  </td>
					  <td className="text-end">
						<div className="d-flex justify-content-end">
						   {hasAuthorization("edit", "page_content") && (
							<TooltipWrapper title="Edit" placement="top">
							  <Link
								className="btn btn-sm btn-icon btn-light btn-active-light-primary me-2"
								to={`/create-update-page/${item._id}`}
							  >
								<i className="ki-outline ki-notepad-edit text-warning fs-2"></i>
							  </Link>
							</TooltipWrapper>
						  )}
						  {hasAuthorization("view", "page_content") && (
							<TooltipWrapper title="View Details" placement="top">
							  <Link
								className="btn btn-sm btn-icon btn-light btn-active-light-primary me-2"
								to={`/product-detail/${item._id}`}
							  >
								<i className="ki-outline ki-eye text-primary fs-2"></i>
							  </Link>
							</TooltipWrapper>
						  )}
						  {hasAuthorization("delete_product", "page_content") && (
							<TooltipWrapper title="Delete Page" placement="top">
							  <span
								className="btn btn-sm btn-icon btn-light btn-active-light-danger"
								onClick={() => deletePage(item?._id)}
							  >
								<i className="ki-outline ki-trash text-danger fs-2"></i>
							  </span>
							</TooltipWrapper>
						  )}
						</div>
					  </td>
					</tr>
				  ))
				)}
			  </tbody>
			</table>
		  </div>
          {/*Content Area End*/}
          {/*Pagination Start*/}
          <div
            className={`sticky-bottom bg-white border rounded p-2 shadow-sm ${
              isLoading ? "" : "border-top"
            }`}
            style={{
              bottom: 0,
              zIndex: 2,
              // backgroundColor: `${isLoading ? "#BBBCBE" : "white"}`,
            }}
          >
            {results && results.length > 0 ? (
              <Pagination
                totalItems={totalItems}
                itemsPerPage={pagination.pageSize}
                onPageChange={handlePageChange}
              />
            ) : null}
          </div>
          {/*Pagination End*/}
          {/*Content Loader Start*/}
          {isLoading ? <ContentLoader /> : null}
          {/*Content Loader End*/}
        </div>
      </div>
   </>
  );
};

export default List;